/**
 * Gates local-library Tauri IPC. Production Connector builds may not include
 * local_library_* commands (integration-only). When ACL rejects a command,
 * all library traffic falls back to the cloud API for the rest of the session.
 */

let _localLibraryIpcAvailable: boolean | null = null;
/** True when this machine has a scanned local SQLite catalog (home library host). */
let _localLibraryHasAlbums: boolean | null = null;
/** Set after the first host probe completes (success or failure). */
let _hostProbeComplete = false;
/** True when Mycelium reports a reachable home-library tunnel (another machine owns the catalog). */
let _remoteHomeLibraryAvailable = false;
/** Persisted remote catalog in cache — hides Add Music while reconnecting to home. */
let _cachedRemoteLibraryHint = false;
let _coreLocalLibraryIpcConfirmed = false;
let _probePromise: Promise<boolean> | null = null;
let _hostProbePromise: Promise<boolean> | null = null;

export function isTauriRuntimeAvailable(): boolean {
  const w = window as Window & { __TAURI__?: unknown; __TAURI_INTERNALS__?: unknown };
  return typeof window !== 'undefined' && !!(w.__TAURI__ || w.__TAURI_INTERNALS__);
}

export function isLocalLibraryCommandError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '');
  return /not allowed by ACL|unknown command|command .+ not found/i.test(msg);
}

/** Wait for host probe before routing library calls (avoids false remote-viewer path). */
export async function ensureLibraryRoutingReady(): Promise<void> {
  if (!isTauriRuntimeAvailable()) return;
  if (_hostProbeComplete) return;
  await probeLocalLibraryHost();
}

export function markLocalLibraryIpcUnavailable(reason?: string): void {
  if (_localLibraryIpcAvailable !== false) {
    console.warn(
      '[libraryService] Local library IPC unavailable — using cloud API',
      reason || '',
    );
  }
  _localLibraryIpcAvailable = false;
}

/** True when this Connector install is the Mac/PC that owns the scanned NAS library. */
export function isHomeLibraryHost(): boolean {
  return _coreLocalLibraryIpcConfirmed === true;
}

/** True after probeLocalLibraryHost() has finished at least once. */
export function isLibraryHostProbeComplete(): boolean {
  return _hostProbeComplete;
}

/**
 * Remote Connector shell: Tauri present, probe complete, no local SQLite catalog,
 * AND a home library is reachable elsewhere (tunnel health / remote catalog).
 * Without the last check, a fresh Mac with no scan yet would incorrectly hide Add Music.
 */
export function isRemoteLibraryViewer(): boolean {
  return isRemoteConnectorShell()
    && (_remoteHomeLibraryAvailable || _cachedRemoteLibraryHint);
}

/**
 * Tauri Connector on a machine that does NOT own the scanned SQLite catalog
 * (remote laptop, or fresh Mac before first scan). Library reads must use
 * Mycelium tunnel / cloud — never local_library_* IPC.
 */
export function isRemoteConnectorShell(): boolean {
  if (!isTauriRuntimeAvailable()) return false;
  if (isHomeLibraryHost()) return false;
  if (_hostProbeComplete) return true;
  // Probe still running — if IPC already failed, treat as remote shell.
  if (_localLibraryIpcAvailable === false) return true;
  return false;
}

export function markCachedRemoteLibraryHint(hasRemoteAlbums: boolean): void {
  if (_cachedRemoteLibraryHint === hasRemoteAlbums) return;
  _cachedRemoteLibraryHint = hasRemoteAlbums;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maestro:remote-home-library-changed'));
  }
}

export function setRemoteHomeLibraryAvailable(available: boolean): void {
  if (_remoteHomeLibraryAvailable === available) return;
  _remoteHomeLibraryAvailable = available;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maestro:remote-home-library-changed'));
  }
}

/** Call when /api/library/remote/catalog returns albums for this account. */
export function markRemoteHomeLibraryFromCatalog(albumCount: number): void {
  if (albumCount > 0) setRemoteHomeLibraryAvailable(true);
}

export function hasRemoteHomeLibraryAvailable(): boolean {
  return _remoteHomeLibraryAvailable;
}

/** True only on the home host after a successful ACL probe — never on remote Connector shells. */
export function shouldUseLocalLibraryIpc(): boolean {
  if (!isTauriRuntimeAvailable()) return false;
  if (!isHomeLibraryHost()) return false;
  if (_localLibraryIpcAvailable === false) return false;
  return true;
}

export function isLocalLibraryIpcUnavailable(): boolean {
  return isTauriRuntimeAvailable() && _localLibraryIpcAvailable === false;
}

/** Probe IPC and album count — distinguishes home host from remote Connector installs. */
export async function probeLocalLibraryHost(): Promise<boolean> {
  if (!isTauriRuntimeAvailable()) {
    _localLibraryHasAlbums = false;
    _hostProbeComplete = true;
    return false;
  }
  if (_coreLocalLibraryIpcConfirmed) return true;
  if (_hostProbeComplete && _localLibraryIpcAvailable === false) return false;
  if (!_hostProbePromise) {
    _hostProbePromise = (async () => {
      const ipcOk = await probeLocalLibraryIpc();
      if (!ipcOk) {
        _localLibraryHasAlbums = false;
        return false;
      }
      try {
        const tauri = (window as Window & { __TAURI__?: { core?: { invoke?: Function } } }).__TAURI__;
        const stats = await tauri?.core?.invoke?.('local_library_get_stats');
        const n = Number((stats as { albums?: number; total_albums?: number })?.albums
          ?? (stats as { total_albums?: number })?.total_albums
          ?? 0);
        _localLibraryHasAlbums = n > 0;
        _coreLocalLibraryIpcConfirmed = true;
        _localLibraryIpcAvailable = true;
        if (n === 0) {
          console.log('[libraryService] Home library host — local IPC OK, catalog empty (Add Music available)');
        } else {
          console.log(`[libraryService] Home library host — ${n} albums in local catalog`);
        }
        return true;
      } catch {
        _localLibraryHasAlbums = false;
        return false;
      } finally {
        _hostProbeComplete = true;
        _hostProbePromise = null;
      }
    })();
  }
  return _hostProbePromise;
}

export async function probeLocalLibraryIpc(): Promise<boolean> {
  if (!isTauriRuntimeAvailable()) return false;
  if (_localLibraryIpcAvailable === false) return false;
  if (_localLibraryIpcAvailable === true) return true;
  if (!_probePromise) {
    _probePromise = (async () => {
      try {
        const tauri = (window as Window & { __TAURI__?: { core?: { invoke?: Function } } }).__TAURI__;
        if (!tauri?.core?.invoke) {
          _localLibraryIpcAvailable = false;
          return false;
        }
        await tauri.core.invoke('local_library_get_stats');
        _localLibraryIpcAvailable = true;
        _coreLocalLibraryIpcConfirmed = true;
        return true;
      } catch (e) {
        if (isLocalLibraryCommandError(e)) {
          markLocalLibraryIpcUnavailable(String((e as Error)?.message || e));
        } else {
          _localLibraryIpcAvailable = false;
        }
        return false;
      } finally {
        _probePromise = null;
      }
    })();
  }
  return _probePromise;
}

export function routeLocalLibrary<T>(
  localFn: () => Promise<T>,
  cloudFn: () => Promise<T>,
  opts?: { poisonOnAclFailure?: boolean },
): Promise<T> {
  // Remote Connector viewing home library elsewhere — never touch local SQLite IPC.
  if (isRemoteConnectorShell() || isRemoteLibraryViewer()) return cloudFn();
  if (!shouldUseLocalLibraryIpc()) return cloudFn();
  const poison = opts?.poisonOnAclFailure !== false;
  return localFn().catch((err) => {
    if (isLocalLibraryCommandError(err)) {
      if (poison) {
        markLocalLibraryIpcUnavailable(String((err as Error)?.message || err));
        _coreLocalLibraryIpcConfirmed = false;
      }
      return cloudFn();
    }
    throw err;
  });
}
